import { useState, useRef, useEffect } from 'react'

interface ColorPickerProps {
  label: string
  value: string
  onChange: (color: string) => void
  presets?: string[]
}

const DEFAULT_PRESETS = [
  '#14b8a6', // Teal
  '#10b981', // Emerald
  '#3b82f6', // Blue
  '#8b5cf6', // Violet
  '#ec4899', // Pink
  '#f59e0b', // Amber
  '#ef4444', // Red
  '#6b7280', // Gray
]

export default function ColorPicker({
  label,
  value,
  onChange,
  presets = DEFAULT_PRESETS,
}: ColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [hexInput, setHexInput] = useState(value)
  const pickerRef = useRef<HTMLDivElement>(null)

  // Sync hex input when value changes externally
  useEffect(() => {
    setHexInput(value)
  }, [value])

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleHexChange = (hex: string) => {
    setHexInput(hex)
    // Only update if valid hex
    if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
      onChange(hex)
    }
  }

  const handleHexBlur = () => {
    // Reset to current value if invalid
    if (!/^#[0-9A-Fa-f]{6}$/.test(hexInput)) {
      setHexInput(value)
    }
  }

  return (
    <div className="relative" ref={pickerRef}>
      <label className="block text-sm font-medium text-brand-muted mb-2">{label}</label>

      {/* Color swatch button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="
          flex items-center gap-3
          w-full
          px-4 py-3
          bg-white
          border border-slate-300
          rounded-lg
          hover:border-accent-teal/50
          transition-colors
        "
      >
        <div
          className="w-8 h-8 rounded-md border border-slate-200"
          style={{ backgroundColor: value }}
        />
        <span className="text-sm text-brand-light font-mono">{value.toUpperCase()}</span>
        <svg
          className={`w-4 h-4 ml-auto text-brand-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Dropdown picker */}
      {isOpen && (
        <div
          className="
            absolute
            top-full
            left-0
            right-0
            mt-2
            p-4
            bg-white
            border border-slate-200
            rounded-lg
            shadow-xl
            z-50
          "
        >
          {/* Preset colors */}
          <div className="grid grid-cols-4 gap-2 mb-4">
            {presets.map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => {
                  onChange(color)
                  setHexInput(color)
                }}
                className={`
                  w-full aspect-square rounded-md
                  border-2 transition-all
                  ${value === color ? 'border-slate-900 scale-110' : 'border-transparent hover:border-slate-400'}
                `}
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
          </div>

          {/* Hex input */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-brand-muted">Hex:</span>
            <input
              type="text"
              value={hexInput}
              onChange={(e) => handleHexChange(e.target.value)}
              onBlur={handleHexBlur}
              placeholder="#14b8a6"
              className="
                flex-1
                px-3 py-2
                bg-white
                border border-slate-200
                rounded-md
                text-sm font-mono text-brand-light
                placeholder:text-brand-subtle
                focus:outline-none focus:border-accent-teal
              "
            />
          </div>

          {/* Native color input (hidden but accessible) */}
          <input
            type="color"
            value={value}
            onChange={(e) => {
              onChange(e.target.value)
              setHexInput(e.target.value)
            }}
            className="
              w-full h-8 mt-3
              cursor-pointer
              rounded-md
              border border-slate-200
            "
          />
        </div>
      )}
    </div>
  )
}
